import React, { useContext } from 'react';
import { SignedInContext } from './context/signed_in_context';
import { UAContext } from './context/ua_context';
import UserEmailForm from './components/user_email_form/user_email_form';
import OptOutOfAllEmails from './components/opt_out_of_all_emails/opt_out_of_all_emails';
import NewsletterBlock from './components/newsletter_block/newsletter_block';
import { toggleNewsletterSubscription } from './services/signed_in.api.service';
import { Newsletter } from './types/types';
import ProNewsletterViewOnly from './components/pro_newsletter_view_only/pro_newsletter_view_only';
import classNames from 'classnames';
import chunk from 'lodash/chunk';
import AlertPopup from '../components/react/alert_popup/alert_popup';

interface Props {
  isSignedIn: boolean;
  formerProUser: boolean;
}

interface Payload {
  referrer: string;
  email?: string;
}

const Container: React.FC<Props> = ({ isSignedIn, formerProUser }: Props) => {
  const {
    isOptedOutOfAllEmails,
    isOptedOutOnFirstMount,
    newsletters,
    setNewsletters,
    deniedPrivileges,
    isLoading,
    preselectedNewsletters,
    setPreselectedNewsletters,
    emailFromUrl,
    alertData,
    setAlertData,
  } = useContext(isSignedIn ? SignedInContext : UAContext);

  const newsletterChunks = chunk(newsletters, 2);

  const onNewsletterToggle = (affectedNewsletter: Newsletter): Promise<Newsletter> => {
    const { id, subscribed, permalink } = affectedNewsletter;
    const payload: Payload = {
      referrer: `${window.location.href}#${permalink}`,
    };

    if (emailFromUrl) {
      payload.email = emailFromUrl;
    }

    return toggleNewsletterSubscription(affectedNewsletter, payload)
      .then((modifiedNewsletter): void => {
        // Because, on POST or DELETE, the BE does not return the `subscribed`
        // field for the newsletter, we have to assume that, on successful request,
        // the BE correctly toggled the `subscribed` field of the newsletter.
        modifiedNewsletter.subscribed = !subscribed;

        const updatedNewsletters = [
          ...newsletters.filter((newsletter) => newsletter.id !== id),
          modifiedNewsletter,
        ];

        setNewsletters(updatedNewsletters.sort((a, b) => a.position - b.position));
      })
      .catch((error): void => {
        setAlertData({
          type: 'error',
          message: 'Your preferences couldn\'t be saved. ' + error?.message
        });
      });
  };

  const onNewsletterPreselection = (affectedNewsletter: Newsletter): Promise<null> => {
    const { id } = affectedNewsletter;
    const modifiedPreselection = preselectedNewsletters.map((item) => item.id).includes(id)
      ? [...preselectedNewsletters.filter((newsletter) => newsletter.id !== id)]
      : [...preselectedNewsletters, affectedNewsletter];

    return Promise.resolve(setPreselectedNewsletters(modifiedPreselection));
  };

  const onNewsletterClick = (affectedNewsletter: Newsletter): Promise<null> => {
    return isSignedIn || !!emailFromUrl
      ? onNewsletterToggle(affectedNewsletter)
      : onNewsletterPreselection(affectedNewsletter);
  };

  /**
   * newsletterChunks is an array that contains arrays of 2 newsletters. That is needed in order
   * for us to determine whether we need to add a `div class='row'`, useful for Desktop version.
   * When rendering, we do `newsletterChunks.map((row))` (row is an array), we insert one
   * `div class=''row'` before doing a `row.map((newsletter))`. Note that the last `row` array
   * inside `newsletterChunks` might only contain 1 newsletter, instead of 2.
   */
  const renderNewsletters = newsletterChunks.map(
    (row: Newsletter[], index: number): React.ReactNode => (
      <div key={index} className='row justify-content-around p-0'>
        {row.map(
          (newsletter: Newsletter): React.ReactNode =>
            newsletter?.required_privilege?.constant_name &&
            deniedPrivileges.includes(newsletter?.required_privilege?.constant_name) ? (
                <ProNewsletterViewOnly
                  formerProUser={formerProUser}
                  key={newsletter.id}
                  newsletter={newsletter}
                />
              ) : (
                <NewsletterBlock
                  isSignedIn={isSignedIn}
                  key={newsletter.id}
                  newsletter={newsletter}
                  onNewsletterToggle={() => onNewsletterClick(newsletter)}
                />
              )
        )}
        {row.length === 1 && <div className='col-lg-5'></div>}
      </div>
    )
  );

  return (
    <>
      <AlertPopup alertData={alertData} setAlertData={setAlertData} />
      <div className='account-newsletters-react ps-35 pe-35 pt-md-5 pb-md-5 pb-48 pt-48'>
        {isSignedIn && isOptedOutOnFirstMount && (
          <div
            data-testid='opt-out-of-all-emails-container'
            className='container account-newsletters-react-container mb-4'
          >
            <div className='col-12'>
              <div className='pt-4 pb-4 ps-35 pe-35 ps-md-5 pe-md-5'>
                <OptOutOfAllEmails />
              </div>
            </div>
          </div>
        )}

        <div
          className={classNames('container account-newsletters-react-container', {
            disabled: isOptedOutOfAllEmails,
          })}
        >
          <div className='col-12'>
            <div className='pt-2 pb-35 ps-3 pe-3 p-lg-45'>
              <fieldset disabled={isLoading || isOptedOutOfAllEmails} className='border-0 p-0'>
                {renderNewsletters}
              </fieldset>
              <div className='row'>
                <div className='col-12'>
                  <div className='ps-lg-5 pt-35'>
                    <small className='text-gray-li'>
                      Please read{' '}
                      <a href='/terms-and-conditions' target='_blank'>
                        Devex Terms of Use
                      </a>{' '}
                      and{' '}
                      <a href='/privacy-statement' target='_blank'>
                        Privacy Policy
                      </a>
                      .
                      {isSignedIn && (
                        <span>
                          {' '}
                          You may unsubscribe from{' '}
                          <a href='/account#notifications'>email communication</a> at any time.
                        </span>
                      )}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {!isSignedIn && !!preselectedNewsletters?.length && <UserEmailForm />}
    </>
  );
};

export default Container;
